import React, { useRef } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import {
  Button,
  Field,
  FluentProvider,
  Input,
  webLightTheme,
  Label,
  Tooltip,
} from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';
import { Control, Controller } from 'react-hook-form';
import { iconButtonStyles } from '../../../app/common/styles/CommonStyleObjects';
import { useUrlValidator } from '../../../utils/validation/urlSanitization';

export interface UrlInputProps {
  control: Control<Record<string, unknown>>;
  name: string;
  label?: string;
  defaultValue?: string;
  required?: boolean;
  placeholder?: string;
  tooltip?: string; // tooltip content displayed via info icon
  allowedDomains?: string[]; // optional allowlist for validation
  maxLength?: number; // optional maximum character count for the URL string
  errorMessage?: string;
}

/**
 * Reusable URL input component with real-time validation.
 */
export const UrlInput = ({
  control,
  name,
  label = 'URL',
  defaultValue,
  required = false,
  placeholder = 'https://example.com/path',
  tooltip,
  allowedDomains,
  maxLength,
  errorMessage,
}: UrlInputProps): JSX.Element => {
  const { validateAndSanitize, error: urlError } = useUrlValidator({ allowedDomains });
  const lastValidatedRef = useRef<{ raw: string; result: true | string } | null>(null);

  // runValidation: RHF validation function.
  // Adds a micro-cache so repeated validations with unchanged raw value skip rework.
  const runValidation = (value: unknown): true | string => {
    const raw = (value as string) ?? '';
    if (lastValidatedRef.current && lastValidatedRef.current.raw === raw) {
      return lastValidatedRef.current.result;
    }
    const trimmed = raw.trim();
    if (!trimmed) {
      const result = required ? `${label} is required` : true;
      lastValidatedRef.current = { raw, result };
      return result;
    }
    const canonical = validateAndSanitize(trimmed);
    const result = canonical ? true : urlError || 'Invalid URL format';
    lastValidatedRef.current = { raw, result };
    return result;
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ onChange, value }) => {
        return (
          <FluentProvider theme={webLightTheme}>
            <Stack tokens={{ childrenGap: 5 }}>
              <Stack tokens={{ childrenGap: 4 }}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
                  <Label required={required} id={`${name}-label`}>
                    {label}
                  </Label>
                  {tooltip && (
                    <Tooltip withArrow content={<span>{tooltip}</span>} relationship="description">
                      <Button
                        appearance="subtle"
                        type="button"
                        icon={<InfoRegular color={iconButtonStyles.color} />}
                        style={{ height: 24, width: 24 }}
                      />
                    </Tooltip>
                  )}
                </Stack>
                <Field required={required} validationMessage={errorMessage || urlError}>
                  <Input
                    value={value || ''}
                    onChange={(_, data) => {
                      validateAndSanitize(data.value.trim());
                      onChange(data.value);
                    }}
                    placeholder={placeholder}
                    aria-invalid={!!urlError && !(!required && !(value || '').trim())}
                    aria-describedby={`${tooltip ? `${name}-tooltip ` : ''}${name}-error`}
                    aria-labelledby={`${name}-label`}
                  />
                </Field>
              </Stack>
            </Stack>
          </FluentProvider>
        );
      }}
      rules={{
        ...(required && { required: `${label} is required` }),
        ...(maxLength && {
          maxLength: {
            value: maxLength,
            message: `${label} must be ${maxLength.toLocaleString()} characters or fewer.`,
          },
        }),
        validate: runValidation,
      }}
    />
  );
};

export default UrlInput;
